import WindowViewModelT from '@/ViewModels/Controls/WindowViewModel';
import ButtonViewModelT from '@/ViewModels/Controls/ButtonViewModel';
import MessageBoxViewModelT from '@/ViewModels/Controls/MessageBoxViewModel';
import ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import ViewViewModel from '@/ViewModels/ViewViewModel';
import ComponentManager from '@/Management/ComponentManager';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import DropDownListViewModelT from '@/ViewModels/Controls/DropDownListViewModel';
import MultiSelectDropDownListViewModelT from '@/ViewModels/Controls/MultiSelectDropDownListViewModel';
import NumericTextBoxViewModelT from '@/ViewModels/Controls/NumericTextBoxViewModel';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import RADApplication from '@/Management/RADApplication';
import { IViewViewModelConstructorParameter } from '#/CoreFrameInterfaces';

interface DropDownEntryDTO {
    Text: string;
    Value: string;
}

interface UserTokenContext {
    Principals: DropDownEntryDTO[];
    Languages: DropDownEntryDTO[];
    ValidUntilInHoursMax: number;
    CanGenerateToken: boolean;
    SelectedPrincipal: string;
    SelectedLanguage: string;
    NeedsPrincipal: boolean;
}

export default class ODataTokenGeneratorViewModel extends ViewViewModel {

    public window: WindowViewModelT;
    public languageLabel: LabelViewModelT;
    public languageDropDown: DropDownListViewModelT;
    public principalsLabel: LabelViewModelT;
    public principalsDropDown: MultiSelectDropDownListViewModelT;
    public validityLabel: LabelViewModelT;
    public validityBox: NumericTextBoxViewModelT;
    
    public createTokenButton: ButtonViewModelT;
    public cancelButton: ButtonViewModelT;

    private WindowViewModel = ComponentManager.getViewModel<typeof WindowViewModelT>('WindowViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    private ButtonViewModel = ComponentManager.getViewModel<typeof ButtonViewModelT>('ButtonViewModel');
    private ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');
    private DropDownListViewModel = ComponentManager.getViewModel<typeof DropDownListViewModelT>('DropDownListViewModel');
    private MultiSelectDropDownListViewModel = ComponentManager.getViewModel<typeof MultiSelectDropDownListViewModelT>('MultiSelectDropDownListViewModel');
    private NumericTextBoxViewModel = ComponentManager.getViewModel<typeof NumericTextBoxViewModelT>('NumericTextBoxViewModel');
    
    private principals: KnockoutObservable<DropDownEntryDTO[]>;
    private selectedPrincipals: KnockoutObservable<string[]>;
    private languages: KnockoutObservable<DropDownEntryDTO[]>;
    private selectedLanguage: KnockoutObservable<string>;
    private validUntilInHours: KnockoutObservable<string>;
    private validUntilInHoursMax: KnockoutObservable<string>;
    private needsPrincipal: KnockoutObservable<boolean>;
    private canCurrentlyGenerateToken: KnockoutComputed<boolean>;
    private canGenerateToken: KnockoutObservable<boolean>;
    
    constructor(params: IViewViewModelConstructorParameter) {
        super(params);

        this.languages = this.observableOrDefault<DropDownEntryDTO[]>([]);
        this.selectedLanguage = this.observableOrDefault<string>('');
        this.principals = this.observableOrDefault<DropDownEntryDTO[]>([]);
        this.selectedPrincipals = this.observableOrDefault<string[]>([]);
        this.validUntilInHours = this.observableOrDefault<string>('1');
        this.validUntilInHoursMax = this.observableOrDefault<string>('1');
        this.needsPrincipal = this.observableOrDefault<boolean>(false);
        this.canGenerateToken = this.observableOrDefault<boolean>(false);
        
        this.canCurrentlyGenerateToken = this.defineComputed(() => {
            const canGenerateToken = this.canGenerateToken();
            const needsPrincipal = this.needsPrincipal();
            const hasPrincipals = this.selectedPrincipals().length > 0;
            return canGenerateToken && (!needsPrincipal || hasPrincipals);
        })
        
        this.window = this.createWindow();

        this.languageLabel = this.createLabel('RAD.RADFOUNDATION.PROBLEMDOMAIN.LOCALIZATION.LANGUAGE_ERGNAME');
        this.languageDropDown = this.createLanguagesDropDown(this.languages, this.selectedLanguage);
        
        this.principalsLabel = this.createLabel('RAD.RADFOUNDATION.PROBLEMDOMAIN.PDPRINCIPAL_ERGNAMEPLURALIZED');
        this.principalsDropDown = this.createPrincipalsDropDown(this.principals, this.selectedPrincipals);
        
        this.validityLabel = this.createLabel('ODATA_TOKEN_GENERATOR_CREATETOKEN_VALIDITY_TEXT');
        this.validityBox = this.createValidityBox(this.validUntilInHours, this.validUntilInHoursMax);
        
        this.createTokenButton = this.createGenerateButton(this.canCurrentlyGenerateToken);
        this.cancelButton = this.createCancelButton();
        this.loadContextData();
    }

    private loadContextData(): void {
        this.window.isBusy(true);

        RADEnvironmentProxy.getInstance().callStaticOperation({
            pdTypeName: 'RAD.RADFoundation.ProblemDomain.User',
            operationName: 'GetUserTokenContext',
            operationParameters: [],
            contextParameters: {}
        }).then(result => {
            if (this.isDisposed)
                return;

            if (!this.validateStatus(result, true)) {
                this.close();
                return;
            }

            const json = result.returnValue;
            const context = JSON.parse(json) as UserTokenContext;
            
            this.canGenerateToken(context.CanGenerateToken)
            this.languages(context.Languages);
            this.selectedLanguage(context.SelectedLanguage);

            this.needsPrincipal(context.NeedsPrincipal);
            this.principals(context.Principals);
            
            if (context.SelectedPrincipal)
                this.selectedPrincipals([context.SelectedPrincipal]);

            this.validUntilInHoursMax(context.ValidUntilInHoursMax.toString());
            
        }).always(() => {
            if (this.isDisposed)
                return;
            
            this.window.isBusy(false);
        });
    }

    private generateTokenAndClose() : void {
        this.window.isBusy(true);

        const principals = this.selectedPrincipals();
        const language = this.selectedLanguage();
        const validUntil = this.validUntilInHours();
        
        RADEnvironmentProxy.getInstance().callStaticOperation({
            pdTypeName: 'RAD.RADFoundation.ProblemDomain.User',
            operationName: 'CreateUserToken',
            operationParameters: [principals, language, validUntil],
            contextParameters: {}
        }).then(result => {
            if (this.isDisposed)
                return;

            if (!this.validateStatus(result, true)) {
                this.close();
                return;
            }

            const msg = result.returnValue;

            RADApplication.getInstance().showMessageBox(MessageBoxViewModelT.createNoticeMessageBox(msg));
            this.close();
        }).always(() => {
            if (this.isDisposed)
                return;

            this.window.isBusy(false);
        });
    }
    
    private close() : void {
        this.window.isOpen(false);
    }

    private createCancelButton(): ButtonViewModelT {
        return new this.ButtonViewModel({
            isFocusable: true,
            isVisible: true,
            label: new this.LabelViewModel({
                ergName: 'CANCEL'
            }),
            image: new this.ImageViewModel({
                source: GlobalConfiguration.inlineImages.WINDOW_CANCEL,
                alternativeText: new this.LabelViewModel({
                    ergName: 'CANCEL'
                })
            }),
            onClick: () => {
              this.close();
            },
            parentLocator: this.locator,
            locator: 'cancel'
        });
    }

    private createGenerateButton(isEnabled: KnockoutObservable<boolean>): ButtonViewModelT {
        return new this.ButtonViewModel({
            isFocusable: true,
            isPrimaryButton: true,
            image: new this.ImageViewModel({
                source: 'die.svg',
                alternativeText: new this.LabelViewModel({
                    ergName: 'ODATA_TOKEN_GENERATOR_CREATETOKEN_TEXT'
                })
            }),
            label: new this.LabelViewModel({
                ergName: 'ODATA_TOKEN_GENERATOR_CREATETOKEN_TEXT'
            }),
            onClick: () => {
                this.generateTokenAndClose();
            },
            parentLocator: this.locator,
            locator: 'createToken',
            isEnabled: isEnabled,
        });
    }

    private createValidityBox(value: KnockoutObservable<string>, max: KnockoutObservable<string>): NumericTextBoxViewModelT {
        return new this.NumericTextBoxViewModel({
            value: value,
            min: 1,
            floatingPrecision:0,
            max: max,
            showSpinners: true,
        });
    }

    private createPrincipalsDropDown(
        principals: KnockoutObservable<DropDownEntryDTO[]>,
        selectedPrincipals: KnockoutObservable<string[]>)
        : MultiSelectDropDownListViewModelT {
        return new this.MultiSelectDropDownListViewModel({
            dataTextField: 'Text',
            dataValueField: 'Value',
            onAdded: added => this.principalsDropDown.values.push(added),
            onRemoved: removed => this.principalsDropDown.values.remove(removed),
            data: principals,
            values: selectedPrincipals
        });
    }

    private createLanguagesDropDown(
        languages: KnockoutObservable<DropDownEntryDTO[]>, 
        selectedLanguage: KnockoutObservable<string>)
        : DropDownListViewModelT {
        return new this.DropDownListViewModel({
            dataTextField: 'Text',
            dataValueField: 'Value',
            data: languages,
            value: selectedLanguage, 
        });
    }

    private createLabel(ergName: string): LabelViewModelT{
        return new this.LabelViewModel({
            ergName: ergName
        });
    }

    private createWindow(): WindowViewModelT {
        return new this.WindowViewModel({
            isModal: true,
            isOpen: true,
            allowClose: true,
            headline: new this.LabelViewModel({
                ergName: 'ODATA_TOKEN_GENERATOR_WINDOW_TITLE'
            }),
            locator: this.locator,
            autoFocus: true,
            width: 450,
            maxWidth: 450
        });
    }
}

ComponentManager.setViewModel('ODataTokenGenerator', ODataTokenGeneratorViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');